import { useCallback, useEffect, useState } from "react";
import {
  getSubscriptionLogs,
  createTopUpOrder,
  verifyTopUpPayment,
  getCreditPurchaseHistory,
  downloadCreditInvoice,
} from "../api/settings";
import { loadRazorpayScript } from "../utils/razorpay";

// Mirrors backend/middleware/checkSubscriptionLimit.js's isUnlimited - a limit
// left unset, or one of these sentinel strings/negative numbers, means the
// plan never blocks on that entitlement (Enterprise-tier).
const isUnlimitedEntitlement = (rawLimit) => {
  if (rawLimit === undefined || rawLimit === null || rawLimit === "") return true;
  const normalized = String(rawLimit).trim().toLowerCase();
  if (["unlimited", "infinite", "na", "n/a"].includes(normalized)) return true;
  const numeric = Number(rawLimit);
  if (isNaN(numeric)) return true;
  return numeric < 0;
};

/**
 * useCreditWallet
 * Shared credit-balance + top-up flow for the header credit pill and the
 * Settings > Interpretation & Credits tab.
 *
 * Self-serve top up (and therefore the payment gateway) is only available
 * to businesses on the Custom Plan — Enterprise plan credits are managed
 * by the account manager, so `canTopUp` stays false for them.
 */
const useCreditWallet = () => {
  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState(0);
  const [isUnlimitedCredits, setIsUnlimitedCredits] = useState(false);
  const [currentPlan, setCurrentPlan] = useState(null);
  // Undefined until/unless the backend adds this field to getSubscriptionLogs -
  // stays falsy (chip/banner simply don't show) until that lands.
  const [autoSendEnabled, setAutoSendEnabled] = useState(false);
  const [pricePerCredit, setPricePerCredit] = useState(49);
  // Center/member plan entitlements - same fields checkSubscriptionLimit.js
  // enforces server-side (business_settings.plan_entitlements), surfaced here
  // so the Add Centre / Add Member pages can block navigation client-side
  // instead of letting the user fill out the whole form and hit a 403.
  const [centerLimit, setCenterLimit] = useState(null);
  const [centersConsumed, setCentersConsumed] = useState(0);
  const [userLimit, setUserLimit] = useState(null);
  const [usersConsumed, setUsersConsumed] = useState(0);
  const [planLogs, setPlanLogs] = useState([]);
  const [purchaseHistory, setPurchaseHistory] = useState([]);
  const [topUpOpen, setTopUpOpen] = useState(false);
  const [paying, setPaying] = useState(false);
  const [statusModal, setStatusModal] = useState({ open: false, status: null, data: {} });

  const refreshWallet = useCallback(async () => {
    // Cache-bust: this GET hits the exact same URL every time (no params), so a
    // browser/proxy cache layer can serve a stale response right after a top-up
    // instead of the just-written balance - a unique query param each call
    // guarantees a real network round trip.
    const response = await getSubscriptionLogs({ _t: Date.now() });
    if (response?.success) {
      const balance = response.data?.creditBalance || {};
      setIsUnlimitedCredits(!!balance.isUnlimited);
      setCredits(balance.isUnlimited ? 0 : (balance.remaining_credits ?? 0));
      setCurrentPlan(response.data?.currentPlan || null);
      setAutoSendEnabled(!!response.data?.autoSendEnabled);
      if (response.data?.pricePerCredit) setPricePerCredit(response.data.pricePerCredit);
      setPlanLogs(response.data?.logs || []);
      setCenterLimit(balance.center_limit ?? null);
      setCentersConsumed(Number(balance.centers_consumed) || 0);
      setUserLimit(balance.user_limit ?? null);
      setUsersConsumed(Number(balance.users_consumed) || 0);
    }
  }, []);

  const refreshPurchaseHistory = useCallback(async () => {
    const response = await getCreditPurchaseHistory({ _t: Date.now() });
    if (response?.success) {
      const mapped = (response.data || []).map((order) => ({
        id: order._id,
        orderId: order.id,
        dateTime: order.last_paid_at
          ? new Date(Number(order.last_paid_at)).toLocaleString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
          : "-",
        transactionId: order.last_payment?.id || order.id,
        creditsAdded: order.interpretation_addon?.count,
        amount: order.interpretation_addon?.total_paid,
        paymentMethod: order.last_payment?.method || "-",
      }));
      setPurchaseHistory(mapped);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([refreshWallet(), refreshPurchaseHistory()]);
      setLoading(false);
    };
    init();
  }, [refreshWallet, refreshPurchaseHistory]);

  // Other flows that consume or add credits (e.g. sending a report for
  // interpretation, or a top-up completing) dispatch this event so every
  // independent instance of this hook — the header pill and the Settings
  // tab — stays in sync without a shared store.
  useEffect(() => {
    const onCreditsChanged = () => {
      refreshWallet();
      refreshPurchaseHistory();
    };
    window.addEventListener("credits:changed", onCreditsChanged);
    return () => window.removeEventListener("credits:changed", onCreditsChanged);
  }, [refreshWallet, refreshPurchaseHistory]);

  const canTopUp = currentPlan?.isCustomPlan === true;
  const isCenterLimitReached =
    !isUnlimitedEntitlement(centerLimit) && centersConsumed >= Number(centerLimit);
  const isUserLimitReached =
    !isUnlimitedEntitlement(userLimit) && usersConsumed >= Number(userLimit);

  const openTopUp = () => setTopUpOpen(true);
  const closeTopUp = () => setTopUpOpen(false);
  const closeStatusModal = () => setStatusModal((s) => ({ ...s, open: false }));

  const handleVerifyPayment = async (paymentResponse) => {
    try {
      const verifyResponse = await verifyTopUpPayment({
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_signature: paymentResponse.razorpay_signature,
      });

      setPaying(false);

      if (verifyResponse?.success) {
        setCredits(verifyResponse.data.newBalance);
        refreshPurchaseHistory();
        // Let any other mounted instance of this hook (e.g. the Settings tab,
        // if the top-up was started from the header pill) refresh too.
        window.dispatchEvent(new Event("credits:changed"));
        setStatusModal({
          open: true,
          status: "topUpSuccess",
          data: {
            amountPaid: verifyResponse.data.amountPaid,
            creditsAdded: verifyResponse.data.creditsAdded,
            newBalance: verifyResponse.data.newBalance,
          },
        });
      } else {
        setStatusModal({ open: true, status: "paymentFailed", data: {} });
      }
    } catch (e) {
      console.error("verifyTopUpPayment error:", e);
      setPaying(false);
      setStatusModal({ open: true, status: "paymentFailed", data: {} });
    }
  };

  const handlePayTopUp = async (quantity) => {
    setPaying(true);
    try {
      const orderResponse = await createTopUpOrder({ credits: quantity });

      if (!orderResponse?.success) {
        setPaying(false);
        setTopUpOpen(false);
        setStatusModal({ open: true, status: "paymentFailed", data: {} });
        return;
      }

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setPaying(false);
        setTopUpOpen(false);
        setStatusModal({ open: true, status: "paymentFailed", data: {} });
        return;
      }

      const { orderId, amount, currency, keyId } = orderResponse.data;

      const rzp = new window.Razorpay({
        key: keyId,
        amount,
        currency,
        order_id: orderId,
        name: "Spandan One",
        description: `${quantity} interpretation credit${quantity === 1 ? "" : "s"}`,
        handler: handleVerifyPayment,
        modal: {
          ondismiss: () => setPaying(false),
        },
        // Razorpay's default UPI block only surfaces the QR flow on desktop -
        // add an explicit "Enter UPI ID" (collect) block so test VPAs like
        // success@razorpay can actually be typed in, not just scanned.
        config: {
          display: {
            blocks: {
              upiCollect: {
                name: "Pay via UPI ID",
                instruments: [{ method: "upi", flows: ["collect"] }],
              },
            },
            sequence: ["block.upiCollect"],
            preferences: {
              show_default_blocks: true,
            },
          },
        },
      });

      rzp.on("payment.failed", () => {
        setPaying(false);
        setStatusModal({ open: true, status: "paymentFailed", data: {} });
      });

      setTopUpOpen(false);
      rzp.open();
    } catch (e) {
      console.error("handlePayTopUp error:", e);
      setPaying(false);
      setTopUpOpen(false);
      setStatusModal({ open: true, status: "paymentFailed", data: {} });
    }
  };

  // Same order-create -> Razorpay -> verify flow as handlePayTopUp, but on a
  // successful payment it calls the caller's own `sendFn` (the report's normal
  // send-for-interpretation submit logic) instead of just showing a top-up
  // success modal - used by the "Pay & Send" flow in the interpretation dialogs.
  const handlePayAndSend = async (quantity, sendFn) => {
    setPaying(true);
    try {
      const orderResponse = await createTopUpOrder({ credits: quantity });

      if (!orderResponse?.success) {
        setPaying(false);
        setStatusModal({ open: true, status: "paymentFailed", data: {} });
        return;
      }

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        setPaying(false);
        setStatusModal({ open: true, status: "paymentFailed", data: {} });
        return;
      }

      const { orderId, amount, currency, keyId } = orderResponse.data;

      const rzp = new window.Razorpay({
        key: keyId,
        amount,
        currency,
        order_id: orderId,
        name: "Spandan One",
        description: `${quantity} interpretation credit${quantity === 1 ? "" : "s"}`,
        handler: async (paymentResponse) => {
          try {
            const verifyResponse = await verifyTopUpPayment({
              razorpay_order_id: paymentResponse.razorpay_order_id,
              razorpay_payment_id: paymentResponse.razorpay_payment_id,
              razorpay_signature: paymentResponse.razorpay_signature,
            });

            if (!verifyResponse?.success) {
              setStatusModal({ open: true, status: "paymentFailed", data: {} });
              return;
            }

            setCredits(verifyResponse.data.newBalance);
            refreshPurchaseHistory();
            window.dispatchEvent(new Event("credits:changed"));

            // Credits are in place - now actually send the report. The caller's
            // sendFn owns its own success/close UI; we only step in if it throws.
            try {
              await sendFn();
            } catch (sendErr) {
              console.error("handlePayAndSend: sendFn failed after payment:", sendErr);
              setStatusModal({
                open: true,
                status: "paymentFailed",
                data: { message: "Payment succeeded but sending the report failed - please try sending again." },
              });
            }
          } finally {
            setPaying(false);
          }
        },
        modal: {
          ondismiss: () => setPaying(false),
        },
        config: {
          display: {
            blocks: {
              upiCollect: {
                name: "Pay via UPI ID",
                instruments: [{ method: "upi", flows: ["collect"] }],
              },
            },
            sequence: ["block.upiCollect"],
            preferences: { show_default_blocks: true },
          },
        },
      });

      rzp.on("payment.failed", () => {
        setPaying(false);
        setStatusModal({ open: true, status: "paymentFailed", data: {} });
      });

      rzp.open();
    } catch (e) {
      console.error("handlePayAndSend error:", e);
      setPaying(false);
      setStatusModal({ open: true, status: "paymentFailed", data: {} });
    }
  };

  const downloadInvoice = async (transaction) => {
    if (!transaction?.orderId) return;

    try {
      const response = await downloadCreditInvoice(transaction.orderId);

      if (!response?.data?.url) {
        console.error(
          "downloadInvoice failed:",
          response?.message || "Invoice URL not found"
        );
        return;
      }

      // Open the generated invoice PDF
      // window.open(response.data?.url, "_blank");
    } catch (error) {
      console.error("downloadInvoice failed:", error);
    }
  };

  return {
    loading,
    credits,
    isUnlimitedCredits,
    currentPlan,
    autoSendEnabled,
    pricePerCredit,
    planLogs,
    canTopUp,
    centerLimit,
    centersConsumed,
    userLimit,
    usersConsumed,
    isCenterLimitReached,
    isUserLimitReached,
    purchaseHistory,
    topUpOpen,
    openTopUp,
    closeTopUp,
    paying,
    handlePayTopUp,
    handlePayAndSend,
    statusModal,
    closeStatusModal,
    downloadInvoice,
    retryTopUp: () => {
      closeStatusModal();
      openTopUp();
    },
  };
};

export default useCreditWallet;
